/**
 * Detalhe de uma conciliação (Task 16, brief 3.8).
 *
 * Reúne, para a MESMA conciliação, o bruto, as deduções (taxa/comissão/custo),
 * o líquido esperado × repasse efetivo, o veredito e os LANÇAMENTOS gerenciais
 * que ela originou — cada lançamento com o pedido/produto navegável (D-16.5).
 *
 * A divergência navega até o pedido que a originou (mesmos registros). Nada
 * aqui executa pagamento nem estorno.
 *
 * Migrado para `.tsx`/JSX real (Cluster E — Financeiro & Sistema, redesign
 * por perfil; ver `docs/architecture/ADR-0011-*.md`): troca de sintaxe
 * (`createElement` → JSX) + ícone por bloco de seção via `SectionTitle`
 * (componente local do arquivo — reuso dentro do mesmo arquivo, não uma
 * abstração nova exportada), no mesmo padrão visual do `kpi-card__icon` de
 * `KpiCard.tsx`: ícone em caixa `bg-brand-wash text-brand-text rounded-control`
 * ao lado do `h3`. Esta tela não tinha nenhum ícone antes (lacuna real — a
 * tela irmã `Conciliacao.tsx` já tinha o selo de veredito). O nome acessível
 * de cada bloco continua sendo o texto do `h3` — o ícone é decorativo
 * (`aria-hidden`). Nenhum `data-*`/aria/texto/rota muda.
 */
// Import explícito de `React` — ver o mesmo comentário em `KpiCard.tsx`.
import React, { type ReactElement, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Receipt, ShoppingBag, type LucideIcon, Wallet } from "lucide-react";
import { RecordTable } from "../components/RecordTable.js";
import { FinancialBoundary } from "../components/FinancialBoundary.js";
import { StatusPanel } from "../components/StatusPanel.js";
import { ReconciliationBadge, SimulatorNote } from "./Conciliacao.js";
import {
  entriesForReconciliation,
  isFinancialSourceAwaitingContract,
  reconciliationById
} from "../financial/dataset.js";
import { MANAGERIAL_ACCOUNT_KIND_LABELS } from "../financial/managerial.js";
import { AWAITING_CONTRACT_LABEL } from "../dashboard/kpis.js";
import { formatBrl, formatDateTime } from "../format.js";
import type { FinancialDataset } from "../financial/dataset.js";
import type { ReconciliacaoDetalheViewProps } from "../financial/view-props.js";
import type { ManagerialEntry } from "../api/types.js";

/** Título de seção com ícone decorativo (reforço visual, o `h3` segue o nome acessível do bloco). */
function SectionTitle({ Icon, children }: { Icon: LucideIcon; children: ReactNode }): ReactElement {
  return (
    <h3 className="flex items-center gap-2 text-lg font-extrabold text-ink">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-control bg-brand-wash text-brand-text">
        <Icon className="h-4 w-4" aria-hidden focusable={false} />
      </span>
      {children}
    </h3>
  );
}

function EntriesTable({ rows }: { rows: ManagerialEntry[] }): ReactElement {
  return (
    <RecordTable<ManagerialEntry>
      caption="Lançamentos gerenciais originados por esta conciliação"
      rows={rows}
      idOf={(row) => row.id}
      emptyMessage="Nenhum lançamento vinculado a esta conciliação."
      columns={[
        {
          key: "account",
          label: "Conta",
          render: (row) => `${row.accountName} (${MANAGERIAL_ACCOUNT_KIND_LABELS[row.accountKind]})`
        },
        {
          key: "amount",
          label: "Valor",
          render: (row) => <span data-entry-amount={row.amountCents}>{formatBrl(row.amountCents)}</span>
        },
        {
          key: "order",
          label: "Pedido",
          render: (row) =>
            row.platformOrderId ? (
              <Link to={`/pedidos/${row.platformOrderId}`} className="font-semibold text-brand-text hover:underline">
                {row.orderExternalId ?? row.platformOrderId}
              </Link>
            ) : (
              "—"
            )
        },
        { key: "memo", label: "Memo", render: (row) => row.memo || "—" }
      ]}
    />
  );
}

export function ConciliacaoDetalheView({ financial, reconciliationId }: ReconciliacaoDetalheViewProps): ReactElement {
  if (isFinancialSourceAwaitingContract(financial)) {
    return (
      <div className="page page--conciliacao-detalhe flex flex-col gap-6" data-page="conciliacao-detalhe">
        <header className="drilldown-header flex flex-col gap-1">
          <h2 className="text-2xl font-extrabold text-ink">Conciliação</h2>
        </header>
        <StatusPanel
          kind="awaiting_source"
          title={`Conciliação ${AWAITING_CONTRACT_LABEL}`}
          detail="A rota do financeiro não respondeu neste ambiente, então não há conciliação para abrir."
        />
      </div>
    );
  }
  const reconciliation = reconciliationById(financial, reconciliationId);
  if (!reconciliation) {
    return (
      <div className="page page--conciliacao-detalhe flex flex-col gap-6" data-page="conciliacao-detalhe">
        <header className="drilldown-header flex flex-col gap-1">
          <h2 className="text-2xl font-extrabold text-ink">Conciliação</h2>
        </header>
        <StatusPanel
          kind="empty"
          title={`Conciliação ${reconciliationId} não está na fonte`}
          detail="A fonte respondeu e não tem este identificador — dizer isso é melhor que mostrar uma tela vazia."
        />
        <Link to="/financeiro/conciliacao" className="w-fit text-sm font-semibold text-brand-text hover:underline">
          Voltar para a conciliação
        </Link>
      </div>
    );
  }
  const entries = entriesForReconciliation(financial, reconciliation.id);
  return (
    <div className="page page--conciliacao-detalhe flex flex-col gap-6" data-page="conciliacao-detalhe">
      <header className="drilldown-header flex flex-col gap-3">
        <Link
          to="/financeiro/conciliacao"
          className="drilldown-header__back inline-flex w-fit items-center gap-1 text-sm font-bold text-muted hover:text-ink"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden focusable={false} />
          Voltar para a conciliação
        </Link>
        <h2 className="text-2xl font-black text-ink sm:text-3xl">{`Conciliação ${reconciliation.id}`}</h2>
        <div
          className="drilldown-header__origin flex flex-wrap items-center gap-2 text-sm text-body"
          data-record-id={reconciliation.id}
        >
          <span>Situação:</span>
          <ReconciliationBadge status={reconciliation.status} />
        </div>
      </header>
      <SimulatorNote financial={financial} />
      {/* Valores e pedido de origem lado a lado; lançamentos (tabela) ocupam a largura toda. */}
      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-2">
        <section
          className="reconciliation-detail__values flex flex-col gap-2 rounded-panel border border-border bg-surface p-4 shadow-s1"
          data-section="valores"
        >
          <SectionTitle Icon={Wallet}>Valores</SectionTitle>
          <p className="m-0 text-sm text-body" data-gross={reconciliation.grossCents}>
            Bruto recebido: {formatBrl(reconciliation.grossCents)}
          </p>
          <p className="m-0 text-sm text-body" data-fee={reconciliation.paymentFeeCents}>
            Taxa do meio de pagamento: {formatBrl(reconciliation.paymentFeeCents)}
          </p>
          <p className="m-0 text-sm text-body" data-commission={reconciliation.commissionCents}>
            Comissão de canal: {formatBrl(reconciliation.commissionCents)}
          </p>
          <p className="m-0 text-sm text-body" data-cost={reconciliation.costCents}>
            Custo das mercadorias: {formatBrl(reconciliation.costCents)}
          </p>
          <p className="m-0 text-sm text-body" data-expected={reconciliation.expectedNetCents}>
            Líquido esperado: {formatBrl(reconciliation.expectedNetCents)}
          </p>
          <p className="m-0 text-sm text-body" data-actual={reconciliation.actualNetCents ?? ""}>
            Repasse efetivo:{" "}
            {reconciliation.actualNetCents === null ? "aguardando repasse" : formatBrl(reconciliation.actualNetCents)}
          </p>
          {reconciliation.status === "diverged" ? (
            <p
              className="field--pending m-0 text-sm font-semibold text-danger"
              data-divergence-reason={reconciliation.divergenceReason}
            >
              Divergência: {reconciliation.divergenceReason}
            </p>
          ) : null}
        </section>
        <section
          className="reconciliation-detail__order flex flex-col gap-2 rounded-panel border border-border bg-surface p-4 shadow-s1"
          data-section="pedido"
        >
          <SectionTitle Icon={ShoppingBag}>Pedido de origem</SectionTitle>
          <p className="m-0 text-sm text-body" data-order-link={`/pedidos/${reconciliation.platformOrderId}`}>
            Pedido:{" "}
            <Link
              to={`/pedidos/${reconciliation.platformOrderId}`}
              className="font-semibold text-brand-text hover:underline"
            >
              {reconciliation.orderExternalId ?? reconciliation.platformOrderId}
            </Link>
          </p>
          {reconciliation.reconciledAt ? (
            <p className="m-0 text-sm text-muted">Conciliado em: {formatDateTime(reconciliation.reconciledAt)}</p>
          ) : null}
        </section>
      </div>
      <section
        className="reconciliation-detail__entries flex flex-col gap-3"
        data-section="lancamentos"
      >
        <SectionTitle Icon={Receipt}>Lançamentos gerenciais</SectionTitle>
        <EntriesTable rows={entries} />
      </section>
    </div>
  );
}

export function ConciliacaoDetalhe(): ReactElement {
  const params = useParams();
  const reconciliationId = params.reconciliationId ?? "";
  return (
    <FinancialBoundary>
      {(financial: FinancialDataset) => (
        <ConciliacaoDetalheView financial={financial} reconciliationId={reconciliationId} />
      )}
    </FinancialBoundary>
  );
}
